import React, { useState, useEffect, useRef } from 'react';
import { Mail, Phone, MapPin, Download, Github, Linkedin, ExternalLink, Globe, Code, Database, Cpu, GitBranch, Network, Brain, Terminal } from 'lucide-react';
import ProfilePic from './assets/photo_de_profil.jpg';
import Buydiz1Img from './assets/buydiz1.jpeg';
import Buydiz2Img from './assets/buydiz2.jpeg';
import Buydiz3Img from './assets/buydiz3.jpeg';
import PlakaImg from './assets/plaka.webp';
import PanierPromoImg from './assets/panierpromo.jpg';

const translations = {
  fr: {
    navigation: {
      home: 'Accueil',
      about: 'À Propos',
      projects: 'Projets',
      skills: 'Compétences',
      contact: 'Contact'
    },
    hero: {
      title: 'Selmane Abdelatif Mamchaoui',
      subtitle: 'Ingénieur d\'état en systèmes informatiques',
      tagline: '',
      contactButton: 'Contactez-moi',
      downloadCV: 'Télécharger mon CV'
    },
    about: {
      title: 'À Propos',
      profile: 'Mon Profil',
      profileText: 'Ingénieur d’État en Informatique de l’ESI Alger, spécialité systèmes informatiques. Compétences en développement mobile (Flutter), IA (réseaux de neurones, imputation de données) et DevOps.',
      currently: '',
      currentlyText: '',
      languages: 'Langues',
      interests: 'Centres d\'intérêts'
    },
    projects: {
      title: 'Mes Projets',
      freelance: 'Projets Freelance',
      personal: 'Projets Personnels/Académiques',
      seeProject: 'Voir le projet',
      notAvailable: 'Non disponible pour le moment'
    },
    skills: {
      title: 'Compétences Techniques',
      categories: {
        'Manipulation de données': 'Manipulation de données',
        'Machine Learning/Deep Learning': 'Machine Learning/Deep Learning',
        'DevOps': 'DevOps',
        'Bases de données': 'Bases de données',
        'Développement': 'Développement',
        'Gestion de versions': 'Gestion de versions',
        'Réseaux': 'Réseaux'
      }
    },
    contact: {
      title: 'Contact',
      coordinates: 'Coordonnées',
      social: 'Réseaux Sociaux',
      form: 'Formulaire de Contact',
      nameLabel: 'Nom',
      emailLabel: 'Email',
      messageLabel: 'Message',
      namePlaceholder: 'Votre nom',
      emailPlaceholder: 'votre.email@example.com',
      messagePlaceholder: 'Votre message...',
      sendButton: 'Envoyer le message'
    },
    footer: {
      downloadCV: 'Télécharger mon CV',
      copyright: '© 2025 Tous droits réservés'
    },
    languages: {
      french: 'Français',
      english: 'English'
    }
  },
  en: {
    navigation: {
      home: 'Home',
      about: 'About',
      projects: 'Projects',
      skills: 'Skills',
      contact: 'Contact'
    },
    hero: {
      title: 'Selmane Abdelatif Mamchaoui',
      subtitle: 'State Engineer in Computer Systems',
      tagline: '',
      contactButton: 'Contact Me',
      downloadCV: 'Download my CV'
    },
    about: {
      title: 'About',
      profile: 'My Profile',
      profileText: 'State Engineer in Computer Science at ESI Algiers, specializing in computer systems. Interests and experience in mobile development (Flutter), AI (neural networks, data imputation), and DevOps. Final‑year internship planned at the University of Luxembourg (SNT).',
      currently: '',
      currentlyText: '',
      languages: 'Languages',
      interests: 'Interests'
    },
    projects: {
      title: 'My Projects',
      freelance: 'Freelance Projects',
      personal: 'Personal/Academic Projects',
      seeProject: 'View Project',
      notAvailable: 'Not available yet'
    },
    skills: {
      title: 'Technical Skills',
      categories: {
        'Manipulation de données': 'Data Manipulation',
        'Machine Learning/Deep Learning': 'Machine Learning/Deep Learning',
        'DevOps': 'DevOps',
        'Bases de données': 'Databases',
        'Développement': 'Development',
        'Gestion de versions': 'Version Control',
        'Réseaux': 'Networking'
      }
    },
    contact: {
      title: 'Contact',
      coordinates: 'Coordinates',
      social: 'Social Networks',
      form: 'Contact Form',
      nameLabel: 'Name',
      emailLabel: 'Email',
      messageLabel: 'Message',
      namePlaceholder: 'Your name',
      emailPlaceholder: 'your.email@example.com',
      messagePlaceholder: 'Your message...',
      sendButton: 'Send Message'
    },
    footer: {
      downloadCV: 'Download my CV',
      copyright: '© 2025 All rights reserved'
    },
    languages: {
      french: 'French',
      english: 'English'
    }
  }
};

// Icon mapping for categories
const categoryIcons = {
  'Manipulation de données': Code,
  'Machine Learning/Deep Learning': Brain,
  'DevOps': Terminal,
  'Bases de données': Database,
  'Développement': Cpu,
  'Gestion de versions': GitBranch,
  'Réseaux': Network
};

const App = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [language, setLanguage] = useState('fr');

  const homeRef = useRef(null);
  const aboutRef = useRef(null);
  const projectsRef = useRef(null);
  const skillsRef = useRef(null);
  const contactRef = useRef(null);

  useEffect(() => {
    const sections = [
      { id: 'home', ref: homeRef },
      { id: 'about', ref: aboutRef },
      { id: 'projects', ref: projectsRef },
      { id: 'skills', ref: skillsRef },
      { id: 'contact', ref: contactRef }
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
            setIsMenuOpen(false);
          }
        });
      },
      { threshold: 0.5 }
    );

    sections.forEach(section => {
      if (section.ref.current) {
        observer.observe(section.ref.current);
      }
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.title = language === 'fr' ? "Portfolio de Selmane" : "Selmane's Portfolio";
  }, [language]);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
      setIsMenuOpen(false);
    }
  };

  const projects = [
    {
      id: 'futurebridge',
      title: language === 'fr' ? 'FutureBridge' : 'FutureBridge',
      period: language === 'fr' ? 'Janvier–Avril 2026' : 'January–April 2026',
      description: language === 'fr'
        ? 'Plateforme SaaS pour architectes intégrant des workflows d\'IA, multi-tenant, avec système d\'accès aux modèles IA basé sur des crédits. Conception et développement du backend incluant l\'éditeur de nœuds de workflow, l\'intégration des APIs (Gemini, OpenAI, Flux, etc.) et le panneau d\'administration. Résolution de problèmes d\'infrastructure et validation par tests de charge.'
        : 'SaaS platform for architects integrating AI workflows, multi-tenant, with a credit-based access system for AI models. Design and development of the backend including the workflow node editor, API integrations (Gemini, OpenAI, Flux, etc.), and the admin panel. Infrastructure troubleshooting and validation through load testing.',
      tools: ['Python (FastAPI)', 'PostgreSQL', 'Redis', 'Next.js', 'Docker', 'Nginx'],
      type: 'freelance',
      placeholder: 'futurebridge-plugin.png',
      link: 'https://fbridge.cloud/main',
    },
    {
      id: 'buydiz',
      title: language === 'fr' ? 'BuyDiz' : 'BuyDiz',
      period: language === 'fr' ? 'Septembre–Novembre 2025' : 'September–November 2025',
      description: language === 'fr'
        ? 'Application mobile marketplace développée avec Flutter/Dart, permettant aux utilisateurs d\'acheter et vendre des produits localement.'
        : 'Mobile marketplace application developed with Flutter/Dart, allowing users to buy and sell local products.',
      tools: ['Flutter', 'Dart', 'Firebase', 'Node.js'],
      type: 'freelance',
      images: [Buydiz1Img, Buydiz2Img, Buydiz3Img]
    },
    {
      id: 'plaka',
      title: language === 'fr' ? 'Plaka' : 'Plaka',
      period: language === 'fr' ? 'Juillet 2025' : 'July 2025',
      description: language === 'fr'
        ? 'Maintenance et amélioration d\'une application mobile allemande de gestion de tâches avec Flutter, Node.js et MongoDB.'
        : 'Maintenance and improvement of a German task management mobile application with Flutter, Node.js, and MongoDB.',
      tools: ['Flutter', 'Node.js', 'MongoDB'],
      type: 'freelance',
      image: PlakaImg,
      link: 'https://apps.apple.com/de/app/plaka/id6740456506'
    },
    {
      id: 'panierpromo',
      title: language === 'fr' ? 'PanierPromo' : 'PanierPromo',
      period: language === 'fr' ? '2023–2024' : '2023–2024',
      description: language === 'fr'
        ? 'Application promotionnelle développée pour le marché français, permettant aux utilisateurs de découvrir et profiter des offres spéciales.'
        : 'Promotional application developed for the French market, allowing users to discover and enjoy special offers.',
      tools: ['Flutter', 'PostgreSQL', 'Docker'],
      type: 'freelance',
      image: PanierPromoImg,
      link: 'https://apps.apple.com/us/app/panierpromo/id6589174479'
    },
    {
      id: 'trafficiprediction',
      title: language === 'fr' ? 'Prédiction du flux de trafic' : 'Traffic Flow Prediction',
      period: '2025',
      description: language === 'fr'
        ? 'Modèle utilisant des Graph Neural Networks pour prédire les flux de trafic en temps réel.'
        : 'Model using Graph Neural Networks to predict real-time traffic flow.',
      tools: ['Python', 'PyTorch', 'GNN', 'Matplotlib'],
      type: 'personal'
    },
    {
      id: 'birdrecognition',
      title: language === 'fr' ? 'Reconnaissance des oiseaux' : 'Bird Recognition',
      period: '2024',
      description: language === 'fr'
        ? 'Système de reconnaissance d\'oiseaux basé sur le Deep Learning utilisant des architectures CNN avancées.'
        : 'Bird recognition system using deep learning with advanced CNN architectures.',
      tools: ['Deep Learning', 'PyTorch', 'TensorFlow', 'OpenCV'],
      type: 'personal'
    },
    {
      id: 'maily',
      title: language === 'fr' ? 'Maily' : 'Maily',
      period: '2023',
      description: language === 'fr'
        ? 'Boîte aux lettres intelligente connectée utilisant Flutter pour l\'interface et C++ pour la gestion matérielle.'
        : 'Smart connected mailbox using Flutter for the interface and C++ for hardware management.',
      tools: ['Flutter', 'C++', 'IoT', 'REST API'],
      type: 'personal'
    }
  ];

  const skills = {
    'Manipulation de données': ['NumPy', 'Pandas', 'Matplotlib', 'Scikit-learn'],
    'Machine Learning/Deep Learning': ['TensorFlow', 'PyTorch', 'Regression models', 'Graph Neural Networks'],
    'DevOps': ['Docker', 'Kubernetes', 'Nginx', 'CI/CD'],
    'Bases de données': ['PostgreSQL', 'MongoDB', 'Firebase', 'SQL'],
    'Développement': ['Flutter', 'Java', 'C/C++', 'Python', 'Dart'],
    'Gestion de versions': ['Git', 'GitHub', 'GitLab'],
    'Réseaux': ['TCP/IP', 'VLAN', 'NAT', 'Network protocols']
  };

  const t = translations[language];

  const FORM_ENDPOINT = 'https://formspree.io/f/xlgrjzoq';
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState('idle'); // idle | sending | success | error
  const [formError, setFormError] = useState('');

  const handleContactChange = (e) => {
    const { id, value } = e.target;
    setContactForm(prev => ({ ...prev, [id]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormStatus('sending');

    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(contactForm),
      });

      if (res.ok) {
        setFormStatus('success');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        const data = await res.json().catch(() => ({}));
        setFormStatus('error');
        setFormError(data.error || 'Submission failed.');
      }
    } catch (err) {
      setFormStatus('error');
      setFormError(err.message || 'Network error.');
    }
  };

  const freelanceProjects = projects.filter(p => p.type === 'freelance');
  const personalProjects = projects.filter(p => p.type === 'personal');
  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div className="eng-app">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

        .eng-app {
          font-family: 'Inter', sans-serif;
          background: #0A1614;
          color: #E1F5EE;
          min-height: 100vh;
        }
        .eng-app h1, .eng-app h2, .eng-app h3 { font-family: 'Inter', sans-serif; font-weight: 600; }
        .eng-mono { font-family: 'JetBrains Mono', monospace; }

        /* NAV */
        .eng-nav {
          display: flex; justify-content: space-between; align-items: center;
          padding: 1.1rem 2rem; border-bottom: 1px solid #1D3B34;
          font-size: 0.85rem; position: sticky; top: 0; background: #0A1614; z-index: 50;
        }
        .eng-nav-links { display: flex; align-items: center; }
        .eng-nav-links button { background: none; border: none; cursor: pointer; color: #9FE1CB; font-family: 'Inter', sans-serif; text-decoration: none; margin: 0 1rem; transition: color 0.2s; font-size: 0.85rem; padding: 0; }
        .eng-nav-links button:hover, .eng-nav-links button.active { color: #E1F5EE; }
        .eng-nav .dot { width: 6px; height: 6px; border-radius: 50%; background: #1D9E75; display: inline-block; margin-right: 6px; }
        .eng-nav .lang-group { display: flex; align-items: center; gap: 6px; }
        .eng-nav .lang-btn { background: none; border: 1px solid #1D3B34; cursor: pointer; padding: 4px 8px; color: #7FA396; font-family: 'JetBrains Mono', monospace; font-size: 0.7rem; transition: all 0.2s; }
        .eng-nav .lang-btn.active { color: #0A1614; background: #1D9E75; border-color: #1D9E75; }
        .eng-burger { display: none; flex-direction: column; gap: 4px; background: none; border: none; cursor: pointer; }
        .eng-burger span { width: 20px; height: 2px; background: #9FE1CB; display: block; transition: all 0.2s; }
        .eng-mobile-menu { display: none; flex-direction: column; border-bottom: 1px solid #1D3B34; background: #0A1614; }
        .eng-mobile-menu.open { display: flex; }
        .eng-mobile-menu button { background: none; border: none; text-align: left; color: #9FE1CB; padding: 0.9rem 2rem; border-top: 1px solid #12241f; font-family: 'Inter', sans-serif; font-size: 0.9rem; }
        .eng-mobile-menu button.active { color: #5DCAA5; }

        /* HERO */
        .eng-hero {
          display: grid; grid-template-columns: 1fr 0.8fr; gap: 3rem;
          padding: 4rem 2rem; border-bottom: 1px solid #1D3B34; max-width: 1100px; margin: 0 auto;
        }
        .eng-hero h1 { font-size: 2.4rem; line-height: 1.15; margin-bottom: 1rem; color: #E1F5EE; }
        .eng-hero .accent { color: #5DCAA5; display: block; font-size: 1.25rem; margin-top: 0.6rem; font-weight: 500; }
        .eng-hero p { font-size: 0.95rem; color: #7FA396; line-height: 1.6; margin-bottom: 1.5rem; max-width: 460px; }

        .eng-photo {
          width: 100%; aspect-ratio: 1; background: #0F241F; border: 1px solid #1D3B34;
          display: flex; align-items: center; justify-content: center; overflow: hidden;
        }
        .eng-photo img { width: 100%; height: 100%; object-fit: cover; filter: grayscale(30%) sepia(8%); }

        .eng-btn-row { display: flex; gap: 0.85rem; flex-wrap: wrap; margin-top: 1.75rem; }
        .eng-btn {
          display: inline-flex; align-items: center; gap: 6px;
          background: transparent; color: #5DCAA5; border: 1px solid #1D9E75;
          padding: 0.7rem 1.4rem; font-size: 0.85rem; font-family: 'JetBrains Mono', monospace;
          cursor: pointer; transition: all 0.2s; text-decoration: none;
        }
        .eng-btn:hover { background: #1D9E75; color: #0A1614; }
        .eng-btn.solid { background: #1D9E75; color: #0A1614; }
        .eng-btn.solid:hover { background: #5DCAA5; border-color: #5DCAA5; }

        /* SECTIONS */
        .eng-section { padding: 3rem 2rem; border-bottom: 1px solid #1D3B34; max-width: 1100px; margin: 0 auto; }
        .eng-section h2 { font-size: 1.3rem; margin-bottom: 2rem; color: #E1F5EE; }
        .eng-section h2::before { content: '// '; color: #1D9E75; font-family: 'JetBrains Mono', monospace; }

        /* ABOUT */
        .about-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2.5rem; }
        .about-grid h3 { font-size: 1rem; margin-bottom: 0.85rem; color: #5DCAA5; font-family: 'JetBrains Mono', monospace; text-transform: lowercase; }
        .about-grid p { font-size: 0.9rem; color: #9FE1CB; line-height: 1.7; }
        .about-block { border-left: 2px solid #1D3B34; padding-left: 1.25rem; margin-bottom: 1.75rem; }
        .about-block:last-child { margin-bottom: 0; }
        .lang-row { display: flex; justify-content: space-between; padding: 0.5rem 0; border-bottom: 1px solid #1D3B34; font-size: 0.9rem; color: #9FE1CB; }
        .lang-row:last-child { border-bottom: none; }

        /* PROJECTS */
        .proj-subtitle { font-family: 'JetBrains Mono', monospace; font-size: 0.75rem; color: #4A6359; text-transform: uppercase; letter-spacing: 0.08em; margin: 2rem 0 1rem; }
        .proj-subtitle:first-child { margin-top: 0; }
        .proj-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1px; background: #1D3B34; border: 1px solid #1D3B34; }
        .proj-cell { background: #0A1614; padding: 1.5rem; transition: background 0.2s; }
        .proj-cell:hover { background: #0F241F; }
        .proj-cell .idx-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem; }
        .proj-cell .idx { font-family: 'JetBrains Mono', monospace; color: #1D9E75; font-size: 0.75rem; }
        .proj-cell .period { font-family: 'JetBrains Mono', monospace; color: #4A6359; font-size: 0.7rem; }
        .proj-cell h3 { font-size: 1.15rem; margin: 0 0 0.6rem; color: #E1F5EE; }
        .proj-cell p { font-size: 0.85rem; color: #7FA396; line-height: 1.55; margin-bottom: 0.9rem; }
        .proj-cell .tools { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 1rem; }
        .proj-cell .tools span { font-family: 'JetBrains Mono', monospace; font-size: 0.68rem; color: #5DCAA5; border: 1px solid #1D3B34; padding: 2px 8px; }
        .proj-cell .proj-link { display: inline-flex; align-items: center; gap: 6px; color: #5DCAA5; font-family: 'JetBrains Mono', monospace; font-size: 0.8rem; text-decoration: none; }
        .proj-cell .proj-link:hover { color: #E1F5EE; }
        .proj-cell .proj-unavailable { color: #4A6359; font-family: 'JetBrains Mono', monospace; font-size: 0.8rem; }
        .proj-imgs { display: flex; gap: 0.6rem; overflow-x: auto; margin-bottom: 1rem; border: 1px solid #1D3B34; padding: 0.6rem; background: #0F241F; }
        .proj-imgs img { height: 220px; width: auto; object-fit: contain; flex-shrink: 0; filter: grayscale(15%); }
        .proj-img-single { border: 1px solid #1D3B34; padding: 0.6rem; margin-bottom: 1rem; background: #0F241F; }
        .proj-img-single img { width: 100%; height: auto; max-height: 320px; object-fit: contain; display: block; margin: 0 auto; filter: grayscale(15%); }

        /* SKILLS */
        .skills-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1px; background: #1D3B34; border: 1px solid #1D3B34; }
        .skill-cell { background: #0A1614; padding: 1.4rem; }
        .skill-cell .head { display: flex; align-items: center; gap: 10px; margin-bottom: 1rem; }
        .skill-cell .head h3 { font-size: 0.95rem; color: #5DCAA5; font-family: 'JetBrains Mono', monospace; text-transform: lowercase; }
        .skill-cell .tags { display: flex; flex-wrap: wrap; gap: 6px; }
        .skill-cell .tags span { font-size: 0.78rem; color: #E1F5EE; border: 1px solid #1D3B34; padding: 4px 10px; background: #0F241F; }

        /* CONTACT */
        .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3rem; }
        .contact-grid h3 { font-size: 1rem; margin-bottom: 1.25rem; color: #5DCAA5; font-family: 'JetBrains Mono', monospace; text-transform: lowercase; }
        .coord-row { display: flex; align-items: center; gap: 12px; margin-bottom: 0.9rem; color: #9FE1CB; font-size: 0.9rem; }
        .coord-row svg { color: #1D9E75; flex-shrink: 0; }
        .social-row { display: flex; gap: 0.75rem; margin-top: 1.5rem; }
        .social-row a { width: 40px; height: 40px; border: 1px solid #1D3B34; display: flex; align-items: center; justify-content: center; color: #9FE1CB; transition: all 0.2s; }
        .social-row a:hover { border-color: #1D9E75; color: #5DCAA5; }
        .eng-field { margin-bottom: 1.1rem; }
        .eng-field label { display: block; font-family: 'JetBrains Mono', monospace; font-size: 0.72rem; color: #4A6359; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 0.4rem; }
        .eng-field input, .eng-field textarea {
          width: 100%; background: #0F241F; border: 1px solid #1D3B34; color: #E1F5EE;
          padding: 0.65rem 0.8rem; font-family: 'Inter', sans-serif; font-size: 0.9rem;
          outline: none; transition: border-color 0.2s;
        }
        .eng-field input::placeholder, .eng-field textarea::placeholder { color: #4A6359; }
        .eng-field input:focus, .eng-field textarea:focus { border-color: #1D9E75; }
        .eng-submit { width: 100%; }
        .eng-alert { padding: 0.75rem 1rem; margin-bottom: 1.1rem; font-size: 0.85rem; font-family: 'JetBrains Mono', monospace; border: 1px solid; }
        .eng-alert.success { color: #5DCAA5; border-color: #1D9E75; background: rgba(29, 158, 117, 0.08); }
        .eng-alert.error { color: #E28B7D; border-color: #7A3B32; background: rgba(122, 59, 50, 0.12); }

        /* FOOTER */
        .eng-footer { text-align: center; padding: 1.75rem; font-size: 0.75rem; color: #4A6359; font-family: 'JetBrains Mono', monospace; }
        .eng-footer a { color: #5DCAA5; text-decoration: none; }
        .eng-footer a:hover { color: #E1F5EE; }

        @media (max-width: 768px) {
          .eng-hero, .about-grid, .proj-grid, .skills-grid, .contact-grid { grid-template-columns: 1fr; }
          .eng-nav-links { display: none; }
          .eng-burger { display: flex; }
        }
      `}</style>

      {/* Navigation */}
      <nav className="eng-nav">
        <div className="eng-mono"><span className="dot"></span>selmane@portfolio</div>

        <div className="eng-nav-links">
          {['home', 'about', 'projects', 'skills', 'contact'].map((item) => (
            <button
              key={item}
              onClick={() => scrollToSection(item)}
              className={activeSection === item ? 'active' : ''}
            >
              {t.navigation[item]}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div className="lang-group">
            <button
              className={`lang-btn ${language === 'fr' ? 'active' : ''}`}
              onClick={() => setLanguage('fr')}
            >
              FR
            </button>
            <button
              className={`lang-btn ${language === 'en' ? 'active' : ''}`}
              onClick={() => setLanguage('en')}
            >
              EN
            </button>
          </div>
          <button className="eng-burger" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="menu">
            <span style={{ transform: isMenuOpen ? 'rotate(45deg) translateY(6px)' : 'none' }}></span>
            <span style={{ opacity: isMenuOpen ? 0 : 1 }}></span>
            <span style={{ transform: isMenuOpen ? 'rotate(-45deg) translateY(-6px)' : 'none' }}></span>
          </button>
        </div>
      </nav>

      <div className={`eng-mobile-menu ${isMenuOpen ? 'open' : ''}`}>
        {['home', 'about', 'projects', 'skills', 'contact'].map((item) => (
          <button
            key={item}
            onClick={() => scrollToSection(item)}
            className={activeSection === item ? 'active' : ''}
          >
            {t.navigation[item]}
          </button>
        ))}
      </div>

      {/* Hero */}
      <section id="home" ref={homeRef} className="eng-hero">
        <div>
          <h1>{t.hero.title}<span className="accent">{t.hero.subtitle}</span></h1>
          <p>{t.hero.tagline}</p>
          <div className="eng-btn-row">
            <button onClick={() => scrollToSection('contact')} className="eng-btn solid">
              <Mail size={14} /> {t.hero.contactButton}
            </button>
            <a href="/CV_MAMCHAOUI_Selmane.pdf" target="_blank" rel="noopener noreferrer" className="eng-btn">
              <Download size={14} /> {t.hero.downloadCV}
            </a>
          </div>
        </div>
        <div className="eng-photo">
          <img src={ProfilePic} alt="Selmane Mamchaoui" style={{ objectPosition: '80% 0%' }} />
        </div>
      </section>

      {/* About */}
      <section id="about" ref={aboutRef} className="eng-section">
        <h2>{t.about.title}</h2>
        <div className="about-grid">
          <div>
            <div className="about-block">
              <h3>{t.about.profile}</h3>
              <p>{t.about.profileText}</p>
            </div>
            <div className="about-block">
              <h3>{t.about.currently}</h3>
              <p>{t.about.currentlyText}</p>
            </div>
          </div>
          <div>
            <h3>{t.about.languages}</h3>
            <div className="lang-row"><span>Français</span></div>
            <div className="lang-row"><span>English</span></div>
            <div className="lang-row"><span>Arabe</span></div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section id="projects" ref={projectsRef} className="eng-section">
        <h2>{t.projects.title}</h2>

        <div className="proj-subtitle">{t.projects.freelance}</div>
        <div className="proj-grid">
          {freelanceProjects.map((project, index) => (
            <div key={project.id} className="proj-cell">
              <div className="idx-row">
                <span className="idx">{pad(index + 1)}</span>
                <span className="period">{project.period}</span>
              </div>
              <h3>{project.title}</h3>

              {project.images && project.images.length ? (
                <div className="proj-imgs">
                  {project.images.map((src, i) => (
                    <img key={i} src={src} alt={`${project.title} ${i + 1}`} loading="lazy" />
                  ))}
                </div>
              ) : project.image ? (
                <div className="proj-img-single">
                  <img src={project.image} alt={project.title} loading="lazy" />
                </div>
              ) : null}

              <p>{project.description}</p>
              <div className="tools">
                {project.tools.map((tool) => (
                  <span key={tool}>{tool}</span>
                ))}
              </div>
              {project.link ? (
                <a href={project.link} target="_blank" rel="noopener noreferrer" className="proj-link">
                  {t.projects.seeProject} <ExternalLink size={13} />
                </a>
              ) : (
                <div className="proj-unavailable">{t.projects.notAvailable}</div>
              )}
            </div>
          ))}
        </div>

        <div className="proj-subtitle">{t.projects.personal}</div>
        <div className="proj-grid">
          {personalProjects.map((project, index) => (
            <div key={project.id} className="proj-cell">
              <div className="idx-row">
                <span className="idx">{pad(freelanceProjects.length + index + 1)}</span>
                <span className="period">{project.period}</span>
              </div>
              <h3>{project.title}</h3>
              <p>{project.description}</p>
              <div className="tools">
                {project.tools.map((tool) => (
                  <span key={tool}>{tool}</span>
                ))}
              </div>
              <div className="proj-unavailable">{t.projects.notAvailable}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Skills */}
      <section id="skills" ref={skillsRef} className="eng-section">
        <h2>{t.skills.title}</h2>
        <div className="skills-grid">
          {Object.entries(skills).map(([category, tools]) => {
            const IconComponent = categoryIcons[category] || Code;
            return (
              <div key={category} className="skill-cell">
                <div className="head">
                  <IconComponent size={16} color="#5DCAA5" />
                  <h3>{t.skills.categories[category] || category}</h3>
                </div>
                <div className="tags">
                  {tools.map((tool) => (
                    <span key={tool}>{tool}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" ref={contactRef} className="eng-section">
        <h2>{t.contact.title}</h2>
        <div className="contact-grid">
          <div>
            <h3>{t.contact.coordinates}</h3>
            <div className="coord-row"><Mail size={17} /><span>selmane203@gmail.com</span></div>
            <div className="coord-row"><Phone size={17} /><span>(+213) 561452074</span></div>
            <div className="coord-row"><MapPin size={17} /><span>Tlemcen, Algérie</span></div>

            <h3 style={{ marginTop: '2rem' }}>{t.contact.social}</h3>
            <div className="social-row">
              <a href="https://github.com/Selmane13" target="_blank" rel="noopener noreferrer">
                <Github size={18} />
              </a>
              <a href="https://www.linkedin.com/in/mamchaoui-selmane-abdelatif-88ab17248/" target="_blank" rel="noopener noreferrer">
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          <div>
            <h3>{t.contact.form}</h3>
            <form onSubmit={handleContactSubmit}>
              {formStatus === 'success' && (
                <div className="eng-alert success">✓ Message sent — thank you!</div>
              )}
              {formStatus === 'error' && (
                <div className="eng-alert error">{formError || 'Submission failed.'}</div>
              )}
              <div className="eng-field">
                <label htmlFor="name">{t.contact.nameLabel}</label>
                <input
                  type="text"
                  id="name"
                  value={contactForm.name}
                  onChange={handleContactChange}
                  required
                  placeholder={t.contact.namePlaceholder}
                />
              </div>
              <div className="eng-field">
                <label htmlFor="email">{t.contact.emailLabel}</label>
                <input
                  type="email"
                  id="email"
                  value={contactForm.email}
                  onChange={handleContactChange}
                  required
                  placeholder={t.contact.emailPlaceholder}
                />
              </div>
              <div className="eng-field">
                <label htmlFor="message">{t.contact.messageLabel}</label>
                <textarea
                  id="message"
                  rows={4}
                  value={contactForm.message}
                  onChange={handleContactChange}
                  required
                  placeholder={t.contact.messagePlaceholder}
                ></textarea>
              </div>
              <button type="submit" disabled={formStatus === 'sending'} className="eng-btn solid eng-submit" style={{ justifyContent: 'center', opacity: formStatus === 'sending' ? 0.5 : 1 }}>
                {formStatus === 'sending' ? '...' : t.contact.sendButton}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="eng-footer">
        <div style={{ marginBottom: '0.5rem' }}>{t.footer.copyright} — selmane_mamchaoui</div>
        <a href="/CV_MAMCHAOUI_Selmane.pdf" target="_blank" rel="noopener noreferrer">{t.footer.downloadCV}</a>
      </footer>
    </div>
  );
};

export default App;